import React, { useContext } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { ROUTES } from "utils/constants";
import GeneralContext from "contexts/general";
import { LoadingWrapper } from "components";
import { GuardProvider, GuardedRoute } from "react-router-guards";
import Auth from "services/auth";
import { isInHiddenList } from "utils/pluginHelpers";
import { trackException, trackTraceException } from "services/trackInsights";
import { exigeNovoLogin } from "services/auth/errosOidc";
import {
  obterContagemRecargas,
  definirContagemRecargas,
  MAX_RECARGAS
} from "utils/contadorRecargas";

const AsyncSilentCallback = React.lazy(() =>
  import("pages/loginSilentCallback")
);
const AsyncAuthentication = React.lazy(() => import("pages/authentication"));
const AsyncHome = React.lazy(() => import("pages/home"));
const AsyncTrackWork = React.lazy(() => import("pages/trackWork"));
const AsyncLogout = React.lazy(() => import("pages/logout"));
const AsyncError = React.lazy(() => import("pages/error"));
const AsyncFeed = React.lazy(() => import("pages/feed"));
const AsyncMilestones = React.lazy(() => import("pages/milestones"));
const AsyncNotifications = React.lazy(() => import("pages/notifications"));
const AsyncNotification = React.lazy(() => import("pages/notification"));
const AsyncRecommendationEmail = React.lazy(() =>
  import("pages/recommendationEmail")
);
const AsyncUtilizeCoupon = React.lazy(() => import("pages/addCoupon"));
const AsyncUtilizeCouponEmail = React.lazy(() =>
  import("pages/addCouponEmail")
);
const AsyncLoginMock = React.lazy(() => import("pages/loginMock"));
const AsyncDocumentsHome = React.lazy(() => import("pages/documents/home"));
const AsyncDocumentsFloorPlans = React.lazy(() =>
  import("pages/documents/floorPlans")
);
const AsyncTermOnboarding = React.lazy(() => import("pages/termOnboarding"));
const AsyncTermOnboardingSuccess = React.lazy(() =>
  import("pages/termOnboardingSuccess")
);
const AsyncTermOnboardingSign = React.lazy(() =>
  import("pages/termOnboardingSign")
);
const AsyncTermOnboardingFisico = React.lazy(() =>
  import("pages/termOnboardingFisico")
);

const AsyncDocumentsManuais = React.lazy(() =>
  import("pages/documents/manuais")
);
const AsyncDocumentsManualProprietario = React.lazy(() =>
  import("pages/documents/manuais_proprietario")
);
const AsyncDocumentsManuaisAreasComuns = React.lazy(() =>
  import("pages/documents/manuais_areas_comuns")
);
const AsyncDocumentsBalancetes = React.lazy(() =>
  import("pages/documents/balancetes")
);
const AsyncIndicacaoOnboarding = React.lazy(() =>
  import("pages/indicacao/onboarding")
);
const AsyncIndicacaoMain = React.lazy(() => import("pages/indicacao/main"));

const AsyncIndicacaoDetails = React.lazy(() =>
  import("pages/indicacao/details")
);

const AsyncVistoriaAntecipada = React.lazy(() =>
  import("pages/va/onboardingVistoria")
);

const AsyncVistoriaAntecipadaLocation = React.lazy(() =>
  import("pages/va/localizacaoVistoria")
);

const AsyncVistoriaAntecipadaBloqueio = React.lazy(() =>
  import("pages/va/bloqueioVistoria")
);

const AsyncGuiaVistoria = React.lazy(() =>
  import("pages/va/guiaVistoria/main")
);

const AsyncGuiaTopicVistoria = React.lazy(() =>
  import("pages/va/guiaVistoria/topic")
);

const AsyncEntradaIdVistoria = React.lazy(() =>
  import("pages/va/entradaIdVistoria")
);

const AsyncChecklistVistoria = React.lazy(() =>
  import("pages/va/checklistVistoria")
);

const AsyncLoginVistoria = React.lazy(() => import("pages/va/login"));

const AsyncLoginVistoriaCohab = React.lazy(() => import("pages/va/loginCohab"));

const AsyncGuiaEntregaDeChaves = React.lazy(() => import("pages/ec/guia/main"));

const AsyncGuiaTopicEntregaDeChaves = React.lazy(() =>
  import("pages/ec/guia/topic")
);

const AsyncLoginEntregaDeChaves = React.lazy(() => import("pages/ec/login"));

const AsyncEntregaDeChavesLocation = React.lazy(() =>
  import("pages/ec/localizacao")
);

const AsyncEntregaDeChavesTerm = React.lazy(() => import("pages/ec/terms"));

const MAX_RELOADS = MAX_RECARGAS;

// eslint-disable-next-line react/prop-types
export default ({ childProps }) => {
  const { generalLoading } = useContext(GeneralContext);

  const getCount = obterContagemRecargas;

  const setCount = definirContagemRecargas;

  const verifyCommonLoginUser = async (user, next) => {
    if (user && user.expired !== undefined) {
      if (user.expired) {
        const count = getCount();

        if (count > MAX_RELOADS) {
          setCount(0);
          const error = {
            message: "User expirou"
          };
          trackException(error);
          trackTraceException("requireLogin", error);
          next.redirect(ROUTES.ERROR);
        } else {
          await Auth.renew()
            .then(async () => {
              setCount();
              window.location.reload();
            })
            .catch(async erro => {
              // Sessão do IdS vencida ou estado OIDC órfão: descarta o estado
              // local antes do login novo, senão ele volta a atrapalhar.
              if (exigeNovoLogin(erro)) {
                // Conta a tentativa: se o IdS devolver um usuário que já
                // chega expirado, o limite interrompe o loop de SSO.
                setCount();
                await Auth.reiniciarLogin();
              } else {
                await Auth.login();
              }
            });
        }
      } else {
        setCount(0);
        await Auth.tokenExpired();
        next();
      }
    } else {
      setCount(0);
      await Auth.login();
    }
  };

  const requireLogin = async (to, from, next) => {
    const user = await Auth.getUser();
    if (user && user.isSoftauth && (user.ec || user.va)) {
      if (from.location.pathname === ROUTES.LOGOUT) {
        next();
      }
      if (user.ec) {
        next.redirect(ROUTES.ENTREGA_DE_CHAVES_TERM);
      } else {
        next.redirect(ROUTES.VISTORIA_ANTECIPADA_ONBOARDING);
      }
      return;
    }
    await verifyCommonLoginUser(user, next);
  };

  const requireLoginOrVistoriador = async (to, from, next) => {
    const user = await Auth.getUser();
    if (user && user.isSoftauth && (user.ec || user.va)) {
      return next();
    }
    await verifyCommonLoginUser(user, next);
  };

  const requireNegotiation = async (to, from, next) => {
    if (isInHiddenList("NegotiationSection")) {
      next.redirect(ROUTES.HOME);
    } else {
      next();
    }
  };

  return (
    <>
      <GuardProvider loading={LoadingWrapper} error={AsyncError}>
        <Switch>
          <GuardedRoute
            path={ROUTES.SILENT}
            exact
            component={AsyncSilentCallback}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.ERROR}
            exact
            component={AsyncError}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.ERROR_HOMOLOG}
            exact
            component={AsyncError}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.LOGOUT}
            exact
            component={AsyncLogout}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.TRACK_WORK}
            exact
            component={AsyncTrackWork}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.HOME}
            exact
            component={AsyncHome}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={`${ROUTES.MILESTONES}/:section`}
            exact
            component={AsyncMilestones}
            props={childProps}
            guards={[requireLogin]}
          />
          <Route
            path={`${ROUTES.USE_COUPON}/:userId`}
            exact
            component={AsyncUtilizeCoupon}
          />
          <Route
            path={`${ROUTES.USE_COUPON_EMAIL}`}
            exact
            component={AsyncUtilizeCouponEmail}
          />
          <GuardedRoute
            path={ROUTES.FEED}
            exact
            component={AsyncFeed}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.RECOMMENDATION}
            component={props => (
              <Redirect to={ROUTES.RECOMMENDATION_NEW_MAIN} {...props} />
            )}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.RECOMMENDATION_EMAIL}
            exact
            component={AsyncRecommendationEmail}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.NOTIFICATIONS}
            exact
            component={AsyncNotifications}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={`${ROUTES.NOTIFICATIONS}/:notificationId`}
            exact
            component={AsyncNotification}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.DOCUMENTS}
            exact
            component={AsyncDocumentsHome}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.DOCUMENTS_FLOOR_PLANS}
            exact
            component={AsyncDocumentsFloorPlans}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.DOCUMENTS_MANUAIS}
            exact
            component={AsyncDocumentsManuais}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.DOCUMENTS_MANUAIS_PROPRIETARIO}
            exact
            component={AsyncDocumentsManualProprietario}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.DOCUMENTS_MANUAIS_AREAS_COMUNS}
            exact
            component={AsyncDocumentsManuaisAreasComuns}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.DOCUMENTS_BALANCETES}
            exact
            component={AsyncDocumentsBalancetes}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.RECOMMENDATION_NEW_ONBOARDING}
            exact
            component={AsyncIndicacaoOnboarding}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.RECOMMENDATION_NEW_MAIN}
            exact
            component={AsyncIndicacaoMain}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.RECOMMENDATION_NEW_DETAILS}
            exact
            component={AsyncIndicacaoDetails}
            props={childProps}
            guards={[requireLogin]}
          />
          <GuardedRoute
            path={ROUTES.LOGIN_MOCK}
            exact
            component={AsyncLoginMock}
            props={childProps}
          />
          <Route
            path={ROUTES.LOGIN}
            exact
            component={AsyncAuthentication}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.TERM_ONBOARDING}
            exact
            component={AsyncTermOnboarding}
            props={childProps}
            guards={[requireLogin, requireNegotiation]}
          />
          <GuardedRoute
            path={ROUTES.TERM_ONBOARDING_SUCCESS}
            exact
            component={AsyncTermOnboardingSuccess}
            props={childProps}
            guards={[requireLogin, requireNegotiation]}
          />
          <GuardedRoute
            path={ROUTES.TERM_ONBOARDING_SIGN}
            exact
            component={AsyncTermOnboardingSign}
            props={childProps}
            guards={[requireLogin, requireNegotiation]}
          />
          <GuardedRoute
            path={ROUTES.TERM_ONBOARDING_FISICO}
            exact
            component={AsyncTermOnboardingFisico}
            props={childProps}
            guards={[requireLogin, requireNegotiation]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_ONBOARDING}
            exact
            component={AsyncVistoriaAntecipada}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_LOCATION}
            exact
            component={AsyncVistoriaAntecipadaLocation}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_BLOQUEIO}
            exact
            component={AsyncVistoriaAntecipadaBloqueio}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_GUIA}
            exact
            component={AsyncGuiaVistoria}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_GUIA_TOPIC}
            exact
            component={AsyncGuiaTopicVistoria}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_ENTRADA_ID}
            exact
            component={AsyncEntradaIdVistoria}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_CHECKLIST}
            exact
            component={AsyncChecklistVistoria}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_CHECKLIST_ITEM}
            exact
            component={AsyncChecklistVistoria}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_LOGIN}
            exact
            component={AsyncLoginVistoria}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.VISTORIA_ANTECIPADA_LOGIN_COHAB}
            exact
            component={AsyncLoginVistoriaCohab}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.ENTREGA_DE_CHAVES_GUIA}
            exact
            component={AsyncGuiaEntregaDeChaves}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.ENTREGA_DE_CHAVES_GUIA_TOPIC}
            exact
            component={AsyncGuiaTopicEntregaDeChaves}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.ENTREGA_DE_CHAVES_LOGIN}
            exact
            component={AsyncLoginEntregaDeChaves}
            props={childProps}
          />
          <GuardedRoute
            path={ROUTES.ENTREGA_DE_CHAVES_LOCATION}
            exact
            component={AsyncEntregaDeChavesLocation}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path={ROUTES.ENTREGA_DE_CHAVES_TERM}
            exact
            component={AsyncEntregaDeChavesTerm}
            props={childProps}
            guards={[requireLoginOrVistoriador]}
          />
          <GuardedRoute
            path="*"
            component={props => <Redirect to={ROUTES.HOME} {...props} />}
            props={childProps}
            guards={[requireLogin]}
          />
        </Switch>
      </GuardProvider>
      {generalLoading && <LoadingWrapper />}
    </>
  );
};
